import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Wand2, ImagePlus } from "lucide-react";

const TONES = [
  "supportive peer",
  "warm mentor",
  "no-nonsense coach",
  "analytical advisor",
  "creative collaborator",
];

const EXPERTISE_SUGGESTIONS = [
  "Difficult conversations",
  "Career growth",
  "Conflict resolution",
  "Performance reviews",
  "Negotiation",
  "Public speaking",
  "Strategic planning",
  "Product strategy",
  "Engineering management",
  "Hiring",
  "Project unblocking",
];
const TRAIT_SUGGESTIONS = [
  "Empathetic",
  "Direct",
  "Analytical",
  "Creative",
  "Patient",
  "Decisive",
  "Curious",
  "Calm",
];

export default function PersonaNew() {
  const navigate = useNavigate();
  const [presets, setPresets] = useState([]);
  const [ownedPresetIds, setOwnedPresetIds] = useState(new Set());
  const [tab, setTab] = useState("preset"); // preset | simple | rich
  const [busy, setBusy] = useState(false);
  const [paidActive, setPaidActive] = useState(false);

  // Simple
  const [sName, setSName] = useState("");
  const [sTone, setSTone] = useState(TONES[0]);

  // Rich
  const [rName, setRName] = useState("");
  const [rRole, setRRole] = useState("");
  const [rIndustry, setRIndustry] = useState("");
  const [rStyle, setRStyle] = useState("");
  const [rTone, setRTone] = useState(TONES[0]);
  const [rExpertise, setRExpertise] = useState([]);
  const [rTraits, setRTraits] = useState([]);
  const [rTagline, setRTagline] = useState("");
  const [rAvatarUrl, setRAvatarUrl] = useState("");
  const [genAvatar, setGenAvatar] = useState(false);

  // Colleague+ Avatar picker
  const [dAge, setDAge] = useState("");
  const [dHair, setDHair] = useState("");
  const [dEyes, setDEyes] = useState("");
  const [dLook, setDLook] = useState("");
  const [candidates, setCandidates] = useState([]); // [{index, mime_type, data_url}]
  const [pickedIndex, setPickedIndex] = useState(-1);
  const [pickedB64, setPickedB64] = useState(""); // raw base64 (no prefix)
  const [pickedMime, setPickedMime] = useState("image/png");
  const [rolls, setRolls] = useState(0);
  const MAX_ROLLS = 3;

  useEffect(() => {
    api
      .get("/personas/presets")
      .then((r) => setPresets(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api
      .get("/billing/me")
      .then((r) => setPaidActive(!!r.data?.paid_active))
      .catch(() => setPaidActive(false));
    // Fetch user's existing personas so we can disable "Meet X" for any
    // preset they've already added to their team.
    api
      .get("/personas")
      .then((r) => {
        const owned = new Set(
          (r.data || [])
            .map((p) => p.preset_id || null)
            .filter((id) => !!id)
        );
        // Also match legacy personas by name/role for pre-fix rows that
        // don't yet have a preset_id stored on them.
        const byPresetShape = (r.data || []).filter(
          (p) => p.is_preset && !p.preset_id
        );
        setOwnedPresetIds((prev) => {
          const next = new Set(owned);
          for (const legacy of byPresetShape) {
            // Attempt best-effort legacy match: search presets by same
            // name+role after they load below.
            next.add(`__legacy__${legacy.name}::${legacy.role}`);
          }
          return next;
        });
      })
      .catch(() => setOwnedPresetIds(new Set()));
  }, []);

  // Legacy matcher — resolves "__legacy__Name::Role" markers to real preset_ids
  // once presets have loaded.
  useEffect(() => {
    if (!presets.length || !ownedPresetIds.size) return;
    let mutated = false;
    const next = new Set(ownedPresetIds);
    for (const marker of ownedPresetIds) {
      if (typeof marker === "string" && marker.startsWith("__legacy__")) {
        const [name, role] = marker.replace("__legacy__", "").split("::");
        const match = presets.find(
          (p) => p.name === name && (p.role || "") === (role || "")
        );
        if (match) {
          next.delete(marker);
          next.add(match.preset_id);
          mutated = true;
        }
      }
    }
    if (mutated) setOwnedPresetIds(next);
  }, [presets, ownedPresetIds]);

  const hirePreset = async (preset) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/personas/from-preset/${preset.preset_id}`);
      toast.success(`${data.name} joined your team`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createSimple = async (e) => {
    e.preventDefault();
    if (!sName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/personas", {
        name: sName.trim(),
        tone: sTone,
        communication_style: sTone,
      });
      // best-effort: auto-generate avatar so they don't land on initials
      try {
        await api.post(`/personas/${data.persona_id}/avatar`);
      } catch {
        /* avatar is optional */
      }
      toast.success(`${data.name} is ready`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createRich = async (e) => {
    e.preventDefault();
    if (!rName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/personas", {
        name: rName.trim(),
        role: rRole.trim() || null,
        industry: rIndustry.trim() || null,
        communication_style: rStyle.trim() || null,
        tone: rTone,
        expertise_areas: rExpertise,
        personality_traits: rTraits,
        tagline: rTagline.trim() || null,
        avatar_url: rAvatarUrl && !pickedB64 ? rAvatarUrl : null,
      });
      // Order of preference for avatar persistence:
      // 1. User picked a candidate → save that exact image
      // 2. Nothing picked / previewed → let backend generate one ("let Colleague choose")
      if (pickedB64) {
        try {
          await api.post(`/personas/${data.persona_id}/avatar/from-b64`, {
            data_b64: pickedB64,
            mime_type: pickedMime,
          });
        } catch {
          /* avatar is optional, persona is still created */
        }
      } else if (!rAvatarUrl) {
        try {
          await api.post(`/personas/${data.persona_id}/avatar`);
        } catch {
          /* optional */
        }
      }
      toast.success(`${data.name} is ready`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleChip = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  // Preview an AI-generated avatar BEFORE saving the persona (fallback / non-paid).
  // Free-trial and non-Colleague+ users: creates a draft persona → single Gemini gen → deletes draft.
  const previewAvatar = async () => {
    if (!rName.trim()) {
      toast.error("Add a name first");
      return;
    }
    setGenAvatar(true);
    try {
      // Create a draft persona to generate the avatar against (still owned by user).
      const draft = await api.post("/personas", {
        name: rName.trim(),
        role: rRole.trim() || null,
        industry: rIndustry.trim() || null,
        communication_style: rStyle.trim() || null,
        tone: rTone,
        expertise_areas: rExpertise,
        personality_traits: rTraits,
        tagline: rTagline.trim() || null,
      });
      const { data } = await api.post(`/personas/${draft.data.persona_id}/avatar`);
      setRAvatarUrl(data.avatar_url);
      // Clear any picked candidate — this is a fresh single-shot result
      setPickedB64("");
      setPickedIndex(-1);
      setCandidates([]);
      // delete the draft — we'll recreate cleanly on submit with the same avatar URL
      try {
        await api.delete(`/personas/${draft.data.persona_id}`);
      } catch {
        /* not critical */
      }
      toast.success("Avatar ready — review and save");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenAvatar(false);
    }
  };

  // Colleague+ flow: generate 3 candidates with demographic hints. No DB writes.
  const generateCandidates = async () => {
    if (!rName.trim()) {
      toast.error("Add a name first");
      return;
    }
    if (rolls >= MAX_ROLLS) {
      toast.error("You've used all 3 tries. Pick one, or let Colleague choose.");
      return;
    }
    setGenAvatar(true);
    try {
      const { data } = await api.post("/personas/avatar/candidates", {
        name: rName.trim(),
        role: rRole.trim() || null,
        industry: rIndustry.trim() || null,
        tone: rTone,
        personality_traits: rTraits,
        customize: {
          age_range: dAge || null,
          hair: dHair.trim() || null,
          eyes: dEyes.trim() || null,
          appearance: dLook.trim() || null,
        },
        count: 3,
      });
      setCandidates(data.candidates || []);
      setPickedIndex(-1);
      setPickedB64("");
      setRAvatarUrl(""); // clear any prior single-shot preview
      setRolls((n) => n + 1);
      toast.success("Pick your favorite");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenAvatar(false);
    }
  };

  const pickCandidate = (c) => {
    setPickedIndex(c.index);
    setPickedMime(c.mime_type || "image/png");
    // Strip the "data:...;base64," prefix — backend expects raw b64 (or accepts the prefix too)
    const raw = (c.data_url || "").split(",")[1] || c.data_url;
    setPickedB64(raw);
    setRAvatarUrl(c.data_url); // for the preview <Avatar>
  };

  const letColleagueChoose = () => {
    // Clear demographic UI state and fall back to the single-shot preview
    setDAge("");
    setDHair("");
    setDEyes("");
    setDLook("");
    setCandidates([]);
    setPickedIndex(-1);
    setPickedB64("");
    previewAvatar();
  };

  return (
    <div data-testid="persona-new-page" className="min-h-screen bg-[#FBF9F6]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="inline-flex items-center gap-1 text-sm text-[#6B655D] hover:text-[#2D2926] mb-6"
        >
          <ArrowLeft size={16} strokeWidth={1.5} /> Back
        </button>
        <header className="mb-10 anim-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A] mb-2">
            New Colleague
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D2926]">
            Who are you bringing on?
          </h1>
        </header>

        {/* Tabs */}
        <div
          role="tablist"
          className="inline-flex bg-[#F2EFE9] rounded-full p-1 mb-8"
        >
          {[
            { id: "preset", label: "Meet a specialist" },
            { id: "simple", label: "Simple" },
            { id: "rich", label: "Customize fully" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2 text-sm transition-colors ${
                tab === t.id
                  ? "bg-[#2D2926] text-white"
                  : "text-[#6B655D] hover:text-[#2D2926]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "preset" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 anim-fade-up">
            {presets.map((p) => {
              const alreadyOwned = ownedPresetIds.has(p.preset_id);
              return (
                <article
                  key={p.preset_id}
                  data-testid={`preset-${p.preset_id}`}
                  className={`surface-card p-6 flex gap-4 items-start ${
                    alreadyOwned ? "opacity-80" : ""
                  }`}
                >
                  <Avatar src={p.avatar_url} name={p.name} size={64} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-serif text-2xl text-[#2D2926]">
                        {p.name}
                      </h3>
                      {alreadyOwned && (
                        <span
                          data-testid={`preset-owned-${p.preset_id}`}
                          className="text-[10px] uppercase tracking-wider bg-[#F2E4C9] text-[#8C6A2F] rounded-full px-2 py-0.5"
                        >
                          On your team
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#6B655D]">
                      {p.role} · {p.industry}
                    </p>
                    <p className="mt-2 text-sm text-[#6B655D] italic">
                      {p.tagline}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.expertise_areas?.slice(0, 4).map((a) => (
                        <span
                          key={a}
                          className="text-xs px-2.5 py-1 rounded-full bg-[#F2EFE9] text-[#6B655D]"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                    {alreadyOwned ? (
                      <button
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        data-testid={`open-${p.preset_id}`}
                        className="mt-4 inline-flex items-center gap-1 border border-[#E6E2D8] text-[#2D2926] hover:border-[#D4A574] rounded-full px-4 py-2 text-sm transition-colors"
                      >
                        Open in Dashboard
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => hirePreset(p)}
                        data-testid={`hire-${p.preset_id}`}
                        className="mt-4 inline-flex items-center gap-1 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-4 py-2 text-sm transition-colors disabled:opacity-60"
                      >
                        <Sparkles size={14} strokeWidth={1.5} /> Meet{" "}
                        {p.name.split(" ")[0]}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {tab === "simple" && (
          <form
            onSubmit={createSimple}
            data-testid="simple-form"
            className="surface-card p-8 max-w-xl anim-fade-up"
          >
            <div className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                  Their name
                </label>
                <input
                  required
                  data-testid="simple-name"
                  value={sName}
                  onChange={(e) => setSName(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574] transition-all"
                  placeholder="e.g. Jules"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                  Tone
                </label>
                <div className="flex flex-wrap gap-2">
                  {TONES.map((t) => (
                    <button
                      type="button"
                      key={t}
                      data-testid={`simple-tone-${t.replace(/\s+/g, "-")}`}
                      onClick={() => setSTone(t)}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                        sTone === t
                          ? "bg-[#2D2926] text-white border-[#2D2926]"
                          : "bg-white border-[#E6E2D8] text-[#6B655D] hover:bg-[#F2EFE9]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="simple-submit"
                className="bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-3 text-sm transition-colors disabled:opacity-60"
              >
                {busy ? "Creating…" : "Create Colleague"}
              </button>
            </div>
          </form>
        )}

        {tab === "rich" && (
          <form
            onSubmit={createRich}
            data-testid="rich-form"
            className="surface-card p-8 anim-fade-up"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name" required>
                <input
                  required
                  data-testid="rich-name"
                  value={rName}
                  onChange={(e) => setRName(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                  placeholder="e.g. Priya"
                />
              </Field>
              <Field label="Role">
                <input
                  data-testid="rich-role"
                  value={rRole}
                  onChange={(e) => setRRole(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                  placeholder="Engineering Manager"
                />
              </Field>
              <Field label="Industry">
                <input
                  data-testid="rich-industry"
                  value={rIndustry}
                  onChange={(e) => setRIndustry(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                  placeholder="SaaS / Healthcare / Logistics…"
                />
              </Field>
              <Field label="Tone">
                <select
                  data-testid="rich-tone"
                  value={rTone}
                  onChange={(e) => setRTone(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                >
                  {TONES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Communication style" full>
                <input
                  data-testid="rich-style"
                  value={rStyle}
                  onChange={(e) => setRStyle(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                  placeholder="e.g. Asks clarifying questions before advising"
                />
              </Field>
              <Field label="Short tagline" full>
                <input
                  data-testid="rich-tagline"
                  value={rTagline}
                  onChange={(e) => setRTagline(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                  placeholder="e.g. The colleague who tells you the truth, kindly."
                />
              </Field>
            </div>

            <ChipGroup
              label="Areas of expertise"
              options={EXPERTISE_SUGGESTIONS}
              selected={rExpertise}
              onToggle={(v) => toggleChip(rExpertise, setRExpertise, v)}
              testIdPrefix="rich-exp"
            />
            <ChipGroup
              label="Personality traits"
              options={TRAIT_SUGGESTIONS}
              selected={rTraits}
              onToggle={(v) => toggleChip(rTraits, setRTraits, v)}
              testIdPrefix="rich-trait"
            />

            {/* AI Avatar */}
            <div className="mt-6 pt-6 border-t border-[#E6E2D8]">
              <div className="flex items-center gap-3 mb-3">
                <label className="text-xs uppercase tracking-wider text-[#9A938A]">
                  Avatar
                </label>
                {paidActive && (
                  <span
                    data-testid="avatar-plus-badge"
                    className="text-[10px] uppercase tracking-wider bg-[#F2E4C9] text-[#8C6A2F] rounded-full px-2 py-0.5"
                  >
                    Colleague+
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mb-5">
                <Avatar src={rAvatarUrl} name={rName || "?"} size={72} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#6B655D]">
                    {paidActive
                      ? "Add a few appearance hints below, then generate 3 options to choose from — or let Colleague pick."
                      : "We'll auto-generate one when you save. Or preview now."}
                  </p>
                </div>
              </div>

              {paidActive ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-[#9A938A] mb-1">
                        Age range
                      </label>
                      <select
                        data-testid="avatar-age"
                        value={dAge}
                        onChange={(e) => setDAge(e.target.value)}
                        className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                      >
                        <option value="">No preference</option>
                        <option value="20s">20s</option>
                        <option value="30s">30s</option>
                        <option value="40s">40s</option>
                        <option value="50s">50s</option>
                        <option value="60s">60s+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-[#9A938A] mb-1">
                        Hair
                      </label>
                      <input
                        data-testid="avatar-hair"
                        value={dHair}
                        onChange={(e) => setDHair(e.target.value.slice(0, 60))}
                        placeholder="e.g. short curly, dark"
                        className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-[#9A938A] mb-1">
                        Eyes
                      </label>
                      <input
                        data-testid="avatar-eyes"
                        value={dEyes}
                        onChange={(e) => setDEyes(e.target.value.slice(0, 40))}
                        placeholder="e.g. warm brown"
                        className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-[#9A938A] mb-1">
                        Look / vibe
                      </label>
                      <input
                        data-testid="avatar-look"
                        value={dLook}
                        onChange={(e) => setDLook(e.target.value.slice(0, 140))}
                        placeholder="e.g. warm smile, glasses, cozy sweater"
                        className="w-full bg-white border border-[#E6E2D8] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A574]"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 items-center">
                    <button
                      type="button"
                      data-testid="avatar-gen-candidates"
                      onClick={generateCandidates}
                      disabled={genAvatar || !rName.trim() || rolls >= MAX_ROLLS}
                      className="inline-flex items-center gap-1.5 text-sm bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-4 py-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <Sparkles size={14} strokeWidth={1.5} />
                      {genAvatar
                        ? "Generating 3 options…"
                        : candidates.length
                          ? `Try 3 more (${rolls}/${MAX_ROLLS} used)`
                          : "Generate 3 avatar choices"}
                    </button>
                    <button
                      type="button"
                      data-testid="avatar-let-colleague"
                      onClick={letColleagueChoose}
                      disabled={genAvatar || !rName.trim()}
                      className="inline-flex items-center gap-1.5 text-sm bg-[#F2EFE9] text-[#2D2926] hover:bg-[#E6E2D8] rounded-full px-4 py-2 transition-colors disabled:opacity-60"
                    >
                      <Wand2 size={14} strokeWidth={1.5} />
                      Let Colleague choose
                    </button>
                    {rolls >= MAX_ROLLS && !pickedB64 && (
                      <span className="text-xs text-[#9A938A]">
                        All 3 tries used — pick one above, or let Colleague choose.
                      </span>
                    )}
                  </div>

                  {candidates.length > 0 && (
                    <div className="mt-5">
                      <p className="text-[11px] uppercase tracking-wider text-[#9A938A] mb-2">
                        Pick your favorite
                      </p>
                      <div
                        data-testid="avatar-candidates-grid"
                        className="grid grid-cols-3 gap-3"
                      >
                        {candidates.map((c) => {
                          const active = pickedIndex === c.index;
                          return (
                            <button
                              key={c.index}
                              type="button"
                              data-testid={`avatar-candidate-${c.index}`}
                              onClick={() => pickCandidate(c)}
                              className={`relative rounded-2xl overflow-hidden aspect-square border-2 transition-all ${
                                active
                                  ? "border-[#D4A574] ring-4 ring-[#D4A574]/25 scale-[1.02]"
                                  : "border-transparent hover:border-[#E6E2D8]"
                              }`}
                            >
                              <img
                                src={c.data_url}
                                alt={`Option ${c.index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              {active && (
                                <span className="absolute bottom-2 right-2 bg-[#D4A574] text-white text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5">
                                  Chosen
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={previewAvatar}
                  disabled={genAvatar || !rName.trim()}
                  data-testid="rich-gen-avatar"
                  className="inline-flex items-center gap-1.5 text-sm bg-[#F2EFE9] text-[#2D2926] hover:bg-[#E6E2D8] rounded-full px-4 py-2 transition-colors disabled:opacity-60"
                >
                  <ImagePlus size={14} strokeWidth={1.5} />
                  {genAvatar ? "Generating…" : rAvatarUrl ? "Regenerate" : "Preview avatar"}
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="rich-submit"
              className="mt-8 inline-flex items-center gap-2 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-3 text-sm transition-colors disabled:opacity-60"
            >
              <Wand2 size={16} strokeWidth={1.5} />
              {busy ? "Creating…" : "Bring them on board"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
        {label}
        {required && " *"}
      </label>
      {children}
    </div>
  );
}

function ChipGroup({ label, options, selected, onToggle, testIdPrefix }) {
  return (
    <div className="mt-6">
      <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = selected.includes(o);
          return (
            <button
              type="button"
              key={o}
              data-testid={`${testIdPrefix}-${o.replace(/\s+/g, "-")}`}
              onClick={() => onToggle(o)}
              className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors ${
                active
                  ? "bg-[#D4A574] text-white border-[#D4A574]"
                  : "bg-white border-[#E6E2D8] text-[#6B655D] hover:bg-[#F2EFE9]"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
